import { FC, useState, useMemo, useEffect } from 'react'
import { RouteTypeEnum, UserType } from '../types'
import { SwaggerRoutes } from '../types'
import { PermissionsToolbar } from './PermissionsToolbar'
import { PermissionsRouteList } from './PermissionsRouteList'
import { useSwaggerPermissions } from '../factory/useSwaggerPermissions'
import { Loading } from '../../../../components/Loading/Loading'
import { LegendsRouterGroup } from './LegendsRouterGroup'

interface UserPermissionsModalProps {
  user: UserType | null
  isOpen: boolean
  onClose: () => void
  routes: SwaggerRoutes[]
  onSavePermissions?: (userId: number, permissions: SwaggerRoutes[]) => void
}

export const UserPermissionsModal: FC<UserPermissionsModalProps> = ({
  user,
  isOpen,
  onClose,
  routes,
  onSavePermissions,
}) => {

  const { getPermissionsByUser, savePermissionsForUser, setLoading, loading } = useSwaggerPermissions()

  const [selectedPermissions, setSelectedPermissions] = useState<number[]>([])
  const [searchTerm, setSearchTerm] = useState('')
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(
    new Set(Object.values(RouteTypeEnum))
  )
  const [expandedTags, setExpandedTags] = useState<Set<string>>(new Set())

  const filteredRoutes = useMemo(
    () =>
      routes.filter((route) =>
        route.url.toLowerCase().includes(searchTerm.toLowerCase()) ||
        route.operationId.toLowerCase().includes(searchTerm.toLowerCase()) ||
        route.tag.toLowerCase().includes(searchTerm.toLowerCase())
      ),
    [routes, searchTerm]
  )

  // Agrupar rotas por type e depois por tag
  const groupedRoutes = useMemo(() => {
    const groups: Record<string, Record<string, SwaggerRoutes[]>> = {}
    filteredRoutes.forEach((route) => {
      if (!groups[route.type]) {
        groups[route.type] = {}
      }
      if (!groups[route.type][route.tag]) {
        groups[route.type][route.tag] = []
      }
      groups[route.type][route.tag].push(route)
    })
    return groups
  }, [filteredRoutes])


  async function loadUserPermissions(userId: number) {
    setLoading(true)
    const userPermissions = await getPermissionsByUser(userId)
    setSelectedPermissions(userPermissions.map((perm) => perm.swaggerRouteId))
    setLoading(false)
  }


  useEffect(() => {
    if (user && isOpen) {
      loadUserPermissions(user.id)
    }
  }, [user, isOpen])

  if (!user) return null

  const handleTogglePermission = (routeId: number) => {
    setSelectedPermissions((prev) =>
      prev.includes(routeId)
        ? prev.filter((id) => id !== routeId)
        : [...prev, routeId]
    )
  }

  const handleSelectAllInGroup = (routeType: string) => {
    let allRoutes: SwaggerRoutes[] = []
    Object.values(groupedRoutes[routeType] || {}).forEach((tagRoutes) => {
      allRoutes = allRoutes.concat(tagRoutes)
    })

    const groupIds = allRoutes.map((r) => r.id)

    const allSelected = groupIds.every((id) =>
      selectedPermissions.includes(id)
    )

    if (allSelected) {
      setSelectedPermissions((prev) =>
        prev.filter((id) => !groupIds.includes(id))
      )
    } else {
      setSelectedPermissions((prev) => [
        ...new Set([...prev, ...groupIds]),
      ])
    }
  }

  const handleSelectAllInTag = (routeType: string, tag: string) => {
    const tagRoutes = groupedRoutes[routeType]?.[tag] || []
    const tagIds = tagRoutes.map((r) => r.id)

    const allSelected = tagIds.every((id) =>
      selectedPermissions.includes(id)
    )

    if (allSelected) {
      setSelectedPermissions((prev) =>
        prev.filter((id) => !tagIds.includes(id))
      )
    } else {
      setSelectedPermissions((prev) => [
        ...new Set([...prev, ...tagIds]),
      ])
    }
  }

  const handleSelectAll = () => {
    if (selectedPermissions.length === filteredRoutes.length) {
      setSelectedPermissions([])
    } else {
      setSelectedPermissions(filteredRoutes.map((route) => route.id))
    }
  }

  const handleSave = async () => {
    const selectedRoutes = routes.filter((route) =>
      selectedPermissions.includes(route.id)
    )
    setLoading(true)
    await savePermissionsForUser(user.id, selectedRoutes)
    setLoading(false)
    onClose()
  }

  const toggleGroup = (routeType: string) => {
    setExpandedGroups((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(routeType)) {
        newSet.delete(routeType)
      } else {
        newSet.add(routeType)
      }
      return newSet
    })
  }

  const toggleTag = (routeType: string, tag: string) => {
    const tagKey = `${routeType}-${tag}`
    setExpandedTags((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(tagKey)) {
        newSet.delete(tagKey)
      } else {
        newSet.add(tagKey)
      }
      return newSet
    })
  }

  const getMethodColor = (method: string) => {
    const colors: Record<string, string> = {
      GET: 'info',
      POST: 'success',
      PUT: 'warning',
      DELETE: 'danger',
      PATCH: 'secondary',
    }
    return colors[method] || 'secondary'
  }

  const getTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      common: 'Comum',
      payment: 'Pagamento',
      hotel: 'Hotel',
      fashion: 'Moda',
      food: 'Alimentos',
    }
    return labels[type] || type
  }

  const isGroupFullySelected = (routeType: string) => {
    let allRoutes: SwaggerRoutes[] = []
    Object.values(groupedRoutes[routeType] || {}).forEach((tagRoutes) => {
      allRoutes = allRoutes.concat(tagRoutes)
    })
    return (
      allRoutes.length > 0 &&
      allRoutes.every((r) => selectedPermissions.includes(r.id))
    )
  }

  const isGroupPartiallySelected = (routeType: string) => {
    let allRoutes: SwaggerRoutes[] = []
    Object.values(groupedRoutes[routeType] || {}).forEach((tagRoutes) => {
      allRoutes = allRoutes.concat(tagRoutes)
    })
    const selectedInGroup = allRoutes.filter((r) =>
      selectedPermissions.includes(r.id)
    )
    return selectedInGroup.length > 0 && selectedInGroup.length < allRoutes.length
  }

  const isTagFullySelected = (routeType: string, tag: string) => {
    const tagRoutes = groupedRoutes[routeType]?.[tag] || []
    return (
      tagRoutes.length > 0 &&
      tagRoutes.every((r) => selectedPermissions.includes(r.id))
    )
  }

  const isTagPartiallySelected = (routeType: string, tag: string) => {
    const tagRoutes = groupedRoutes[routeType]?.[tag] || []
    const selectedInTag = tagRoutes.filter((r) =>
      selectedPermissions.includes(r.id)
    )
    return selectedInTag.length > 0 && selectedInTag.length < tagRoutes.length
  }

  const isAllSelected = selectedPermissions.length === filteredRoutes.length && filteredRoutes.length > 0

  return (
    <>
      {/* Modal Backdrop */}
      {isOpen && (
        <div
          className='modal-backdrop fade show'
          onClick={onClose}
          style={{ opacity: 0.5 }}
        />
      )}

      {/* Modal */}
      <div
        className={`modal fade ${isOpen ? 'show d-block' : ''}`}
        tabIndex={-1}
        style={{ display: isOpen ? 'block' : 'none' }}
      >
        <div className='modal-dialog modal-fullscreen-xl-down modal-xl'>
          <div className='modal-content'>
            {/* Header */}
            <div className='modal-header bg-light'>
              <div>
                <h5 className='modal-title'>Gerenciar Permissões</h5>
                <small className='text-muted'>{user.name}</small>
              </div>
              <button
                type='button'
                className='btn-close'
                onClick={onClose}
                aria-label='Fechar'
              />
            </div>

            {/* Body */}
            <div className='modal-body'>
              {/* Search */}
              <div className='mb-3'>
                <input
                  type='text'
                  className='form-control'
                  placeholder='Buscar por URL, operação ou tag...'
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>

              {/* Legend */}
              <LegendsRouterGroup />

              {/* Toolbar */}
              <PermissionsToolbar
                selectedCount={selectedPermissions.length}
                totalCount={routes.length}
                onSelectAll={handleSelectAll}
                isAllSelected={isAllSelected}
              />
              {loading ? (<Loading />) : (
                <PermissionsRouteList
                  groupedRoutes={groupedRoutes}
                  expandedGroups={expandedGroups}
                  expandedTags={expandedTags}
                  selectedPermissions={selectedPermissions}
                  onToggleType={toggleGroup}
                  onSelectAllType={handleSelectAllInGroup}
                  onToggleTag={toggleTag}
                  onSelectAllTag={handleSelectAllInTag}
                  onToggleRoute={handleTogglePermission}
                  isGroupFullySelected={isGroupFullySelected}
                  isGroupPartiallySelected={isGroupPartiallySelected}
                  isTagFullySelected={isTagFullySelected}
                  isTagPartiallySelected={isTagPartiallySelected}
                  getMethodColor={getMethodColor}
                  getTypeLabel={getTypeLabel}
                />
              )}
            </div>

            {/* Footer */}
            <div className='modal-footer bg-light'>
              <button
                type='button'
                className='btn btn-outline-secondary'
                onClick={onClose}
              >
                Cancelar
              </button>
              <button
                type='button'
                className='btn btn-primary'
                onClick={handleSave}
                disabled={selectedPermissions.length === 0 || loading}
              >
                Salvar Permissões
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
